import { apiUrl, env } from '@/config/env';

import { ApiError, networkError } from './api-error';
import type { ApiBody, ApiPaginationMeta } from './api-types';
import { authToken } from './auth-token';

/**
 * Client HTTP unique de l'application.
 *
 * Aucun composant ni hook n'appelle `fetch` directement : tout passe par ici.
 * Cela centralise l'URL de base, le token, le déballage de l'enveloppe de
 * réponse et la conversion des erreurs en `ApiError`.
 */

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** Paramètres de query string. Les valeurs `undefined`/`null` sont ignorées. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Corps de requête. Sérialisé en JSON, sauf `FormData` (laissé tel quel). */
  body?: unknown;
  /** Délai maximal avant abandon, en millisecondes. */
  timeoutMs?: number;
  /** `true` pour une route publique : n'envoie pas l'en-tête `Authorization`. */
  skipAuth?: boolean;
  /**
   * `true` pour ne pas tenter de rafraîchir la session sur un 401.
   *
   * Posé automatiquement sur le **rejeu** d'une requête : sans lui, un serveur
   * qui répond 401 pour une autre raison que l'expiration ferait boucler le
   * couple « rafraîchir, rejouer » indéfiniment.
   */
  skipRefresh?: boolean;
  /** Préfixe l'URL par l'origine seule (utile pour `/healthz`, hors `/api/v1`). */
  absolutePath?: boolean;
}

const DEFAULT_TIMEOUT_MS = 20_000;

/**
 * Fonction de rafraîchissement de session, injectée par le module
 * d'authentification.
 *
 * ── Pourquoi une injection plutôt qu'un import ───────────────────────────
 * Le client HTTP ne connaît pas les routes d'authentification, et il ne doit
 * pas : c'est lui que `authApi.refresh()` utilise pour parler au serveur. Un
 * import direct créerait un cycle. L'inversion garde `lib/api` ignorant de
 * `features/auth`, qui est le sens de dépendance voulu.
 */
type SessionRefresher = () => Promise<unknown>;

let refreshSession: SessionRefresher | null = null;

/** Branche le rafraîchissement. Appelé une fois, par `features/auth`. */
export const setSessionRefresher = (refresher: SessionRefresher | null): void => {
  refreshSession = refresher;
};

/**
 * Tente un rafraîchissement. `false` si aucun n'est branché ou s'il échoue.
 *
 * N'échoue jamais bruyamment : un rafraîchissement raté n'est pas une panne à
 * remonter, c'est une session terminée. L'appelant rend alors le 401 initial,
 * qui porte déjà le bon message.
 */
const tryRefresh = async (): Promise<boolean> => {
  if (!refreshSession) return false;

  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
};

const buildUrl = (path: string, options: RequestOptions): string => {
  const base = options.absolutePath ? env.VITE_API_BASE_URL : apiUrl;
  const url = `${base}${path}`;

  if (!options.query) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query)) {
    if (value !== undefined && value !== null) search.append(key, String(value));
  }

  const queryString = search.toString();
  return queryString ? `${url}?${queryString}` : url;
};

const buildHeaders = (options: RequestOptions): Headers => {
  const headers = new Headers(options.headers);

  // FormData doit conserver le Content-Type généré par le navigateur
  // (il contient la frontière multipart) : on ne le force qu'en JSON.
  const isFormData = options.body instanceof FormData;
  if (options.body !== undefined && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const token = authToken.get();
  if (token && !options.skipAuth) headers.set('Authorization', `Bearer ${token}`);

  return headers;
};

const serializeBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined) return undefined;
  if (body instanceof FormData) return body;
  return JSON.stringify(body);
};

/** Convertit une réponse non-2xx en `ApiError`, en respectant l'enveloppe serveur. */
const toApiError = async (response: Response): Promise<ApiError> => {
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // Réponse sans corps JSON (502 d'un proxy, HTML d'erreur…).
  }

  // Après le `in`, TypeScript sait déjà que `parsed.error` existe : pas d'assertion.
  const isEnvelope =
    typeof parsed === 'object' &&
    parsed !== null &&
    'error' in parsed &&
    parsed.error !== null &&
    typeof parsed.error === 'object';

  if (isEnvelope) {
    const body = parsed as Extract<ApiBody<unknown>, { success: false }>;
    return new ApiError({
      message: body.error.message,
      status: response.status,
      code: body.error.code,
      ...(body.error.details ? { details: body.error.details } : {}),
      ...(body.requestId ? { requestId: body.requestId } : {}),
    });
  }

  return new ApiError({
    message: `Erreur ${response.status} (${response.statusText || 'sans détail'}).`,
    status: response.status,
    code: 'INTERNAL_ERROR',
  });
};

const request = async <T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta?: ApiPaginationMeta | Record<string, unknown> }> => {
  const {
    query: _query,
    body,
    timeoutMs,
    skipAuth: _skipAuth,
    skipRefresh: _skipRefresh,
    absolutePath: _absolute,
    ...rest
  } = options;

  // `AbortSignal.timeout` évite de gérer un setTimeout et son nettoyage à la main.
  const signal = rest.signal ?? AbortSignal.timeout(timeoutMs ?? DEFAULT_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options), {
      ...rest,
      method,
      headers: buildHeaders(options),
      body: serializeBody(body),
      // Indispensable : le refresh token voyage dans un cookie httpOnly.
      credentials: 'include',
      signal,
    });
  } catch (error) {
    throw networkError(error);
  }

  if (response.status === 204) return { data: undefined as T };

  /* ── Session expirée : on rafraîchit et on rejoue, une fois ──────────────
     Le token d'accès ne vit que quinze minutes. Sans ce rattrapage, toute
     session dépassant ce délai voyait chacun de ses appels échouer, sans
     qu'aucun code ne répare : seul un rechargement de page, qui rejoue le
     rafraîchissement au démarrage, remettait l'application en marche.

     La condition sur `authToken` n'est pas décorative : dès qu'un
     rafraîchissement échoue, le token est vidé, et les 401 suivants cessent
     d'en déclencher un nouveau. C'est ce qui empêche une salve de requêtes
     d'enchaîner les tentatives sur une session définitivement close. */
  if (
    response.status === 401 &&
    !options.skipAuth &&
    !options.skipRefresh &&
    authToken.get() !== null &&
    (await tryRefresh())
  ) {
    return request<T>(method, path, { ...options, skipRefresh: true });
  }

  if (!response.ok) throw await toApiError(response);

  const payload = (await response.json()) as ApiBody<T>;

  // Garde-fou : un 2xx hors enveloppe signale une désynchronisation de contrat.
  if (!payload.success) throw await toApiError(response);

  return payload.meta === undefined
    ? { data: payload.data }
    : { data: payload.data, meta: payload.meta };
};

export const httpClient = {
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return (await request<T>('GET', path, options)).data;
  },

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>('POST', path, { ...options, body })).data;
  },

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>('PUT', path, { ...options, body })).data;
  },

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return (await request<T>('PATCH', path, { ...options, body })).data;
  },

  async delete<T = void>(path: string, options?: RequestOptions): Promise<T> {
    return (await request<T>('DELETE', path, options)).data;
  },

  /**
   * Variante des listes : conserve la pagination présente dans `meta`.
   *
   * Le second paramètre de type sert aux listes dont l'enveloppe porte plus
   * que la pagination — la boîte de réception y joint son nombre de non lus.
   * Il vaut `ApiPaginationMeta` par défaut : les appels existants ne changent
   * pas.
   */
  async getPaginated<T, M extends ApiPaginationMeta = ApiPaginationMeta>(
    path: string,
    options?: RequestOptions,
  ): Promise<{ items: T[]; meta: M }> {
    const { data, meta } = await request<T[]>('GET', path, options);
    return { items: data, meta: meta as M };
  },
};
